import { useCallback } from "react";

import { DestinationVendorsResponse, ExistingDestination, useDestinationVendors } from "@prequel/react";
import { Status, StatusLight, TableRow } from "@prequel/shared-components";

import { useDestinationsByRecipient } from "@/api/queries/destinations";
import ResourceTable from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";
import VendorLogo from "@/components/VendorLogo";
import { env } from "@/env";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { getReadableLastSuccessfulSync } from "@/types";
import { getReadableVendor, getVendorLogoUrl } from "@/types/common";

const DESTINATION_TABLE_COLUMNS = ["Destination Name", "Vendor", "Last Successful Sync", "Status", "Created at"];

const renderVendorCell = (vendor: string, vendors: DestinationVendorsResponse) => (
  <div className="flex items-center">
    <VendorLogo logo_url={getVendorLogoUrl(vendor, vendors ?? [])} />
    {getReadableVendor(vendor, vendors ?? [])}
  </div>
);

const mapDestinationToRow = (
  destination: ExistingDestination,
  destinationVendors: DestinationVendorsResponse,
): TableRow => ({
  id: destination.id,
  to: `/destinations/${destination.id}`,
  cells: [
    { value: destination.name },
    { value: renderVendorCell(destination.vendor, destinationVendors) },
    {
      value: (
        <div className="text-gray-500">
          {getReadableLastSuccessfulSync(destination.last_successful_transfer_ended_at ?? undefined)}
        </div>
      ),
    },
    {
      value: (
        <div className="flex items-center text-gray-500">
          <StatusLight status={destination.is_enabled ? Status.ACTIVE : Status.INACTIVE} />
          {destination.is_enabled ? "Enabled" : "Disabled"}
        </div>
      ),
    },
    { value: <Timestamp timestamp={destination.created_at} /> },
  ],
});

// Create separate wrapper components for each case to avoid calling hooks in callbacks
const DestinationsByRecipientTable = ({
  recipientId,
  destinationVendors,
}: {
  recipientId: string;
  destinationVendors: DestinationVendorsResponse;
}) => {
  const rowMapper = useCallback(
    (destination: ExistingDestination) => mapDestinationToRow(destination, destinationVendors),
    [destinationVendors],
  );

  // Create the query function that calls the hook directly
  const useQuery = (params: { cursor?: string }) => useDestinationsByRecipient(recipientId, params);

  return (
    <ResourceTable<ExistingDestination>
      columns={DESTINATION_TABLE_COLUMNS}
      useQuery={useQuery}
      rowMapper={rowMapper}
      emptyMessage="No destinations found for this recipient."
    />
  );
};

const RecipientsDestinationsTable = () => {
  const recipientId = useRequiredParam("recipientID");
  const destinationVendors = useDestinationVendors(env.VITE_APP_API_SERVER);

  return <DestinationsByRecipientTable recipientId={recipientId} destinationVendors={destinationVendors} />;
};

export default RecipientsDestinationsTable;
